import React, { useEffect, useRef, useState } from "react";
import { AlertCircle, Inbox, PackageCheck, Timer, Truck, Workflow } from "lucide-react";
import { D2C_OPS_ACTIVITY_API_URL } from "../../Shared/constant";
import { Badge, toneOf } from "./ui/badge";
import { Card, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { SegmentedControl, SegmentedItem } from "./ui/toggle-group";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, clickableRow } from "./ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { cn } from "./ui/utils";
import { KpiCard } from "./HeadlineTiles";
import Pagination from "./Pagination";
import StageProgress from "./StageProgress";
import StateBlock from "./StateBlock";
import useD2cGet from "./useD2cGet";
import { buildQuery, formatDateTime, formatNumber, formatRelative, isPast, stageTone } from "./d2cFormat";
import { activityKpis } from "./stageSteps";

export const ACTIVITY_REFRESH_MS = 60000;
const PAGE_SIZE = 50;

const dealerLabel = (row) => {
  if (row.dealer) return [row.dealer.franchise_code, row.dealer.name].filter(Boolean).join(" · ") || "-";
  const offered = Number(row.offered_to) || 0;
  if (!offered) return "-";
  return `Offered to ${offered} dealer${offered === 1 ? "" : "s"}`;
};

// A relative time with the exact IST time on hover and for screen readers.
export const RelativeTime = ({ iso, now, late = false }) => {
  if (!iso) return <span className="tw-text-muted-foreground">-</span>;
  const exact = formatDateTime(iso);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          data-late={late ? "true" : undefined}
          className={cn("tw-inline-flex tw-items-center tw-gap-1", late && "tw-font-medium tw-text-red-600")}
        >
          {late && <AlertCircle className="tw-size-3.5" aria-hidden="true" />}
          {formatRelative(iso, now)}
        </span>
      </TooltipTrigger>
      <TooltipContent>{exact} IST</TooltipContent>
    </Tooltip>
  );
};

// Latest dealer requests first, from ops/activity. Refreshes itself every
// minute while `visible` (its tab is showing and the browser tab is in front).
const ActivityList = ({ visible, onOpen, refreshTick, onAutoRefresh }) => {
  const [openOnly, setOpenOnly] = useState(true);
  const [paging, setPaging] = useState({ openOnly: true, page: 1 });
  const page = paging.openOnly === openOnly ? paging.page : 1;
  const setPage = (next) => setPaging({ openOnly, page: next });
  const [now, setNow] = useState(() => new Date());

  const url = D2C_OPS_ACTIVITY_API_URL + buildQuery({ open: openOnly ? 1 : "", page, limit: PAGE_SIZE });
  const { data, loading, error, reload } = useD2cGet(url);
  const items = data?.items || [];
  const total = Number(data?.total) || 0;
  const kpis = activityKpis(items, { openOnly, total, now });

  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const autoRef = useRef(onAutoRefresh);
  autoRef.current = onAutoRefresh;
  useEffect(() => {
    if (!visible) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState === "hidden") return;
      setNow(new Date());
      reloadRef.current();
      if (autoRef.current) autoRef.current();
    }, ACTIVITY_REFRESH_MS);
    return () => clearInterval(timer);
  }, [visible]);

  // The page header's Refresh reloads this list too.
  useEffect(() => {
    if (!refreshTick) return;
    setNow(new Date());
    reloadRef.current();
  }, [refreshTick]);

  return (
    <div className="tw-space-y-4">
      <div className="tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-4">
        <KpiCard label="In progress" value={formatNumber(kpis.inProgress)} icon={Workflow} tone="primary" />
        <KpiCard label="Waiting for a dealer" value={formatNumber(kpis.waiting)} icon={Timer} />
        <KpiCard label="Out for delivery" value={formatNumber(kpis.outForDelivery)} icon={Truck} />
        <KpiCard
          label="Delivered today"
          value={kpis.deliveredToday === null ? "-" : formatNumber(kpis.deliveredToday)}
          note={kpis.deliveredToday === null ? "Shown with All recent" : null}
          icon={PackageCheck}
        />
      </div>

      <Card>
        <CardHeader className="tw-items-center">
          <div>
            <CardTitle>
              Requests <span className="tw-font-normal tw-text-muted-foreground">({total.toLocaleString("en-IN")})</span>
            </CardTitle>
            <CardDescription>
              Latest activity first. Refreshes every minute while this tab is open. Counts above are from the requests
              listed here.
            </CardDescription>
          </div>
          <SegmentedControl
            aria-label="Which requests"
            value={openOnly ? "open" : "all"}
            onValueChange={(value) => setOpenOnly(value === "open")}
          >
            <SegmentedItem value="open">In progress</SegmentedItem>
            <SegmentedItem value="all">All recent</SegmentedItem>
          </SegmentedControl>
        </CardHeader>

        <StateBlock
          loading={loading}
          error={error}
          onRetry={reload}
          isEmpty={items.length === 0}
          emptyIcon={Inbox}
          emptyText={openOnly ? "No request is in progress right now." : "No dealer requests yet."}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>City</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead>Dealer</TableHead>
                <TableHead>Offered at</TableHead>
                <TableHead>Last activity</TableHead>
                <TableHead>Deadline</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row) => {
                const late = isPast(row.deadline_at, now);
                return (
                  <TableRow
                    key={row.fulfilment_id}
                    role="button"
                    aria-label={`Open order ${row.order_code || row.order_id}`}
                    tabIndex={0}
                    className={clickableRow}
                    onClick={() => onOpen(row.order_id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onOpen(row.order_id);
                      }
                    }}
                  >
                    <TableCell className="tw-font-mono tw-text-xs tw-font-medium tw-text-primary">
                      {row.order_code || row.order_id}
                    </TableCell>
                    <TableCell>{row.product || "-"}</TableCell>
                    <TableCell>{row.city || row.pincode || "-"}</TableCell>
                    <TableCell>
                      <div className="tw-flex tw-flex-col tw-gap-1">
                        <Badge tone={toneOf(stageTone(row.stage))} title={row.stage} className="tw-self-start">
                          {row.stage_label || row.stage || "-"}
                        </Badge>
                        <StageProgress stage={row.stage} />
                      </div>
                    </TableCell>
                    <TableCell className={row.dealer ? "" : "tw-text-muted-foreground"}>{dealerLabel(row)}</TableCell>
                    <TableCell className="tw-text-muted-foreground">{formatDateTime(row.offered_at)}</TableCell>
                    <TableCell>
                      <RelativeTime iso={row.last_activity_at} now={now} />
                    </TableCell>
                    <TableCell>
                      <RelativeTime iso={row.deadline_at} now={now} late={late} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <Pagination page={page} limit={PAGE_SIZE} total={total} onPage={setPage} />
        </StateBlock>
      </Card>
    </div>
  );
};

export default ActivityList;
